import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import { X, Save, AlertCircle, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { colors } from '../../theme/colors';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
`;

const Required = styled.span`
  color: #ef4444;
  margin-left: 0.25rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.error ? '#ef4444' : '#d1d5db'};
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#ef4444' : '#3b82f6'};
    box-shadow: 0 0 0 3px ${props => props.error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  }

  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #374151;
  }
`;

const ColorPickerWrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ColorInput = styled.input`
  width: 80px;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  
  &::-webkit-color-swatch-wrapper {
    padding: 2px;
  }
  
  &::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
  }
`;

const RandomColorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #374151;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const ColorPreview = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.color};
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
`;

const PasswordStrength = styled.div`
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: ${props => {
    if (props.strength === 'strong') return '#10b981';
    if (props.strength === 'medium') return '#f59e0b';
    return '#ef4444';
  }};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StrengthBar = styled.div`
  flex: 1;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
`;

const StrengthFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: ${props => {
    if (props.strength === 'strong') return '#10b981';
    if (props.strength === 'medium') return '#f59e0b';
    return '#ef4444';
  }};
  transition: all 0.3s;
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FieldError = styled.div`
  margin-top: 0.25rem;
  color: #ef4444;
  font-size: 0.875rem;
`;

const HelpText = styled.div`
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.875rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
  
  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
`;

const SaveButton = styled(Button)`
  background: ${colors.primary};
  border: none;
  color: white;
  
  &:hover:not(:disabled) {
    background: ${colors.primaryHover};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${colors.primaryShadow};
  }
`;

const AVATAR_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', 
  '#ef4444', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

const ROLES = [
  { value: 'case_agent', label: 'Case Agent' },
  { value: 'lead', label: 'Lead' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'client', label: 'Client' },
];

function CreateUserModal({ isOpen, onClose, onSave, loading = false }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    role: 'case_agent',
    avatar_color: '#3b82f6'
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        username: '',
        email: '',
        full_name: '',
        password: '',
        confirmPassword: '',
        role: 'case_agent',
        avatar_color: AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)]
      });
      setErrors({});
      setShowPassword(false);
      setShowConfirmPassword(false);
      setPasswordStrength(null);
      setApiError('');
    }
  }, [isOpen]);

  const calculatePasswordStrength = (password) => {
    if (!password) return null;
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    
    if (strength >= 5) return { level: 'strong', width: 100 };
    if (strength >= 3) return { level: 'medium', width: 60 };
    return { level: 'weak', width: 30 };
  };

  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(formData.password));
  }, [formData.password]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setApiError('');
  };

  const generateRandomColor = () => {
    const randomColor = AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
    handleChange('avatar_color', randomColor);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9])/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const userData = {
        username: formData.username.trim(),
        email: formData.email.trim() || null,
        full_name: formData.full_name.trim(),
        password: formData.password,
        role: formData.role,
        avatar_color: formData.avatar_color
      };

      await onSave(userData);
      onClose();
    } catch (error) {
      // Handle FastAPI validation errors (array of error objects)
      let errorMessage = 'Failed to create user';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setApiError(errorMessage);
    }
  };

  const getInitials = () => {
    if (!formData.full_name) return '?';
    return formData.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Create New User</ModalTitle>
          <CloseButton onClick={onClose} disabled={loading}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        {apiError && (
          <ErrorMessage>
            <AlertCircle size={16} />
            {apiError}
          </ErrorMessage>
        )}

        <FormGroup>
          <Label>
            Username<Required>*</Required>
          </Label>
          <Input
            type="text"
            value={formData.username}
            onChange={(e) => handleChange('username', e.target.value)}
            placeholder="Enter username"
            error={errors.username}
            disabled={loading}
            data-cy="create-user-username"
          />
          {errors.username && <FieldError>{errors.username}</FieldError>}
          <HelpText>Username will be used for login</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="user@example.com"
            error={errors.email}
            disabled={loading}
            data-cy="create-user-email"
          />
          {errors.email && <FieldError>{errors.email}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label>
            Full Name<Required>*</Required>
          </Label>
          <Input
            type="text"
            value={formData.full_name}
            onChange={(e) => handleChange('full_name', e.target.value)}
            placeholder="Enter full name"
            error={errors.full_name}
            disabled={loading}
            data-cy="create-user-fullname"
          />
          {errors.full_name && <FieldError>{errors.full_name}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label>
            Password<Required>*</Required>
          </Label>
          <PasswordInputWrapper>
            <Input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              placeholder="Enter password"
              error={errors.password}
              disabled={loading}
              data-cy="create-user-password"
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </PasswordToggle>
          </PasswordInputWrapper>
          {errors.password && <FieldError>{errors.password}</FieldError>}
          {passwordStrength && (
            <PasswordStrength strength={passwordStrength.level}>
              <StrengthBar>
                <StrengthFill strength={passwordStrength.level} width={passwordStrength.width} />
              </StrengthBar>
              <span>{passwordStrength.level.charAt(0).toUpperCase() + passwordStrength.level.slice(1)}</span>
            </PasswordStrength>
          )}
          <HelpText>Min 8 chars, include uppercase, lowercase, number, and special character</HelpText>
        </FormGroup>

        <FormGroup>
          <Label>
            Confirm Password<Required>*</Required>
          </Label>
          <PasswordInputWrapper>
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e.target.value)}
              placeholder="Confirm password"
              error={errors.confirmPassword}
              disabled={loading}
              data-cy="create-user-confirm-password"
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={loading}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </PasswordToggle>
          </PasswordInputWrapper>
          {errors.confirmPassword && <FieldError>{errors.confirmPassword}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label>
            Role<Required>*</Required>
          </Label>
          <Select
            value={formData.role}
            onChange={(e) => handleChange('role', e.target.value)}
            disabled={loading}
            data-cy="create-user-role"
          >
            {ROLES.map(role => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>
        </FormGroup>

        <FormGroup>
          <Label>Avatar Color</Label>
          <ColorPickerWrapper>
            <ColorPreview color={formData.avatar_color}>
              {getInitials()}
            </ColorPreview>
            <ColorInput
              type="color"
              value={formData.avatar_color}
              onChange={(e) => handleChange('avatar_color', e.target.value)}
              disabled={loading}
              data-cy="create-user-avatar-color"
            />
            <RandomColorButton
              type="button"
              onClick={generateRandomColor}
              disabled={loading}
            >
              <RefreshCw size={16} />
              Random
            </RandomColorButton>
          </ColorPickerWrapper>
        </FormGroup>

        <ButtonGroup>
          <CancelButton type="button" onClick={onClose} disabled={loading}>
            Cancel
          </CancelButton>
          <SaveButton
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            data-cy="create-user-submit"
          >
            {loading ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save size={16} />
                Create User
              </>
            )}
          </SaveButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default CreateUserModal;

